import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "./auth/supabase";
import { useAuth } from "./auth/AuthContext";
import logo from "./assets/logo.png";
import "./AppShell.css";

/* ============================================================
   AppShell — the installable mobile app, at /app (login required).
   Tabs: Check · Scan · History · Account
   Uses the same /api/check, /api/factcheckArticle and
   public.saved_checks as the website.
   ============================================================ */

const VERDICT = {
  true: { key: "fact", label: "Fact" },
  false: { key: "not", label: "Not" },
  ambivalent: { key: "mixed", label: "Mixed" },
};
const CATEGORY = { health: "Health", science: "Science", politics: "Politics", economy: "Economy", climate: "Climate", technology: "Technology" };
const cat = (c) => CATEGORY[String(c || "").toLowerCase()] || "Other";
const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };

/* ---------- Find each claim's sentence in the article (same idea as the extension) ---------- */
const words = (s) => s.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, " ").split(/\s+/).filter((w) => w.length > 2);
const jaccard = (a, b) => { const A = new Set(a), B = new Set(b); let i = 0; A.forEach((x) => B.has(x) && i++); return i / (A.size + B.size - i || 1); };
function locateClaims(paragraphs, claims) {
  const sentences = [];
  paragraphs.forEach((p, pi) => {
    const re = /[^.!?]+[.!?]+["”»']?|[^.!?]+$/g; let m;
    while ((m = re.exec(p))) {
      const lead = m[0].length - m[0].trimStart().length;
      sentences.push({ pi, start: m.index + lead, end: m.index + m[0].trimEnd().length, w: words(m[0]) });
    }
  });
  const marks = paragraphs.map(() => []); const used = new Set(); let found = 0;
  claims.forEach((c, ci) => {
    const cw = words(c.statement || ""); let best = -1, score = 0.12;
    sentences.forEach((s, si) => { if (used.has(si)) return; const sc = jaccard(cw, s.w); if (sc > score) { score = sc; best = si; } });
    if (best >= 0) { used.add(best); found++; const s = sentences[best]; marks[s.pi].push({ start: s.start, end: s.end, ci }); }
  });
  marks.forEach((m) => m.sort((a, b) => a.start - b.start));
  return { marks, found };
}
function MarkedPara({ text, marks, claims, sel, onPick }) {
  const out = []; let pos = 0;
  marks.forEach((m, k) => {
    if (m.start > pos) out.push(text.slice(pos, m.start));
    const key = (VERDICT[claims[m.ci].label] || VERDICT.ambivalent).key;
    out.push(<mark key={k} className={`fa-mark ${key}${sel === m.ci ? " on" : ""}`} onClick={() => onPick(m.ci)}>{text.slice(m.start, m.end)}</mark>);
    pos = m.end;
  });
  out.push(text.slice(pos));
  return <p className="fa-para">{out}</p>;
}

async function authHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const Icon = ({ d, size = 22, sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: d }} />
);
const I = {
  check: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
  scan: '<path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3"/><path d="M8 10h8M8 14h5"/>',
  hist: '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/><path d="M12 8v4l3 2"/>',
  acct: '<circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/>',
  back: '<path d="M15 18l-6-6 6-6"/>',
  chev: '<path d="M9 6l6 6-6 6"/>',
  ok: '<path d="M5 12l5 5L20 7"/>',
  link: '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>',
};

const Wordmark = ({ size = 24 }) => (
  <span className="fa-wordmark" style={{ fontSize: size }}>
    <span className="g">Fact</span><span className="a">Or</span><span className="r">Not</span>
  </span>
);
const Pill = ({ truth, big }) => {
  const v = VERDICT[truth] || VERDICT.ambivalent;
  return <span className={`fa-pill ${v.key}${big ? " big" : ""}`}><span className="pip" />{v.label}</span>;
};

/* ---------- Verdict detail (used by Check + History) ---------- */
function Detail({ item, onBack, saved, onDelete }) {
  const v = VERDICT[item.truth] || VERDICT.ambivalent;
  const sources = [item.source1, item.source2].filter(Boolean);
  return (
    <div className="fa-detail">
      <div className={`fa-detail-head ${v.key}`}>
        <button className="fa-round" onClick={onBack} aria-label="Back"><Icon d={I.back} /></button>
        <div className="fa-big-verdict"><span className="dot" />{v.label}.</div>
        <p className="fa-detail-claim">“{item.claim}”</p>
      </div>
      <div className="fa-pad fa-stack">
        <div className="fa-row-wrap">
          <span className="fa-cat">{cat(item.category)}</span>
          {saved && <span className="fa-saved"><Icon d={I.ok} size={14} sw={2.4} />Saved to history</span>}
        </div>
        <p className="fa-explain">{item.explanation}</p>
        {sources.length > 0 && (
          <div>
            <div className="fa-mono">Sources</div>
            <div className="fa-list">
              {sources.map((s, i) => (
                <a key={i} href={s} target="_blank" rel="noreferrer" className="fa-list-row">
                  <span className="fa-mono">{String(i + 1).padStart(2, "0")}</span>
                  <span className="grow">{host(s)}</span>
                  <Icon d={I.chev} size={18} />
                </a>
              ))}
            </div>
          </div>
        )}
        {onDelete && <button className="fa-btn ghost danger" onClick={onDelete}>Delete from history</button>}
      </div>
    </div>
  );
}

/* ---------- Check tab ---------- */
function CheckTab({ user }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const run = async (text) => {
    const claim = (text ?? q).trim();
    if (!claim) return;
    setBusy(true); setErr("");
    try {
      const r = await fetch("/api/check", { method: "POST", headers: await authHeaders(), body: JSON.stringify({ query: claim }) });
      const data = await r.json();
      if (!r.ok || data.error || !data.truth) {
        setErr(data.error || "We couldn’t find enough reliable sources. Try a more specific claim.");
        return;
      }
      const item = { claim, ...data };
      setResult(item);
      await supabase.from("saved_checks").insert({
        user_id: user.id, claim, truth: data.truth, explanation: data.explanation || "",
        source1: data.source1 || "", source2: data.source2 || "", category: data.category || "other",
      });
    } catch {
      setErr("Couldn’t reach the fact-checking service. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (result) return <Detail item={result} saved onBack={() => { setResult(null); setQ(""); }} />;

  return (
    <div className="fa-pad fa-top">
      <div className="fa-brandbar"><img src={logo} alt="" className="fa-logo" /><Wordmark /></div>
      <h1 className="fa-h1">What did you <em>hear?</em></h1>
      <div className="fa-inputcard">
        <textarea value={q} onChange={(e) => setQ(e.target.value)} placeholder="Paste a claim, a quote, or a headline…" />
        <button className="fa-btn" onClick={() => run()} disabled={busy || !q.trim()}>{busy ? "Checking sources…" : "Check it"}</button>
      </div>
      {err && <div className="fa-error">{err}</div>}
      <div className="fa-chips">
        {["Honey never spoils.", "The Great Wall is visible from space.", "Carrots improve your eyesight."].map((t) => (
          <button key={t} className="fa-chip" onClick={() => { setQ(t); run(t); }} disabled={busy}>{t}</button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Scan tab ---------- */
function ScanTab({ initialUrl }) {
  const [url, setUrl] = useState(initialUrl || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [view, setView] = useState("article");
  const located = useMemo(() => (data?.paragraphs?.length ? locateClaims(data.paragraphs, data.claims) : null), [data]);
  const showArticle = view === "article" && located && located.found > 0;

  const scan = async (u) => {
    const link = (u ?? url).trim();
    if (!/^https?:\/\//i.test(link)) { setErr("Paste a full link starting with https://"); return; }
    setBusy(true); setErr(""); setData(null); setSel(null); setView("article");
    try {
      const r = await fetch("/api/factcheckArticle", { method: "POST", headers: await authHeaders(), body: JSON.stringify({ url: link, maxClaims: 8 }) });
      const raw = await r.text();
      let json = {};
      try { json = JSON.parse(raw); } catch {}
      if (r.status === 504) { setErr("The scan took too long. Try again — the second time is faster thanks to caching."); return; }
      if (r.status === 401) { setErr("Your session expired. Sign out and back in."); return; }
      if (!r.ok || json.error) { setErr(json.error || `Something went wrong (error ${r.status}).`); return; }
      if (!json.claims?.length) { setErr("We read the page but found no checkable claims."); return; }
      setData({ ...json, url: link });
    } catch {
      setErr("No connection to FactOrNot. Check your internet and try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (initialUrl) scan(initialUrl); }, [initialUrl]); // eslint-disable-line

  if (data) {
    const c = sel != null ? data.claims[sel] : null;
    const counts = ["true", "ambivalent", "false"].map((k) => [k, data.claims.filter((x) => x.label === k).length]);
    return (
      <div className="fa-scan-wrap">
        <div className="fa-pad fa-top">
          <button className="fa-link-btn" onClick={() => { setData(null); setUrl(""); }}><Icon d={I.back} size={18} />New scan</button>
          <div className="fa-mono" style={{ marginTop: 14 }}>{host(data.url)}</div>
          {data.title && <h2 className="fa-h2">{data.title}</h2>}
          {data.summary && <p className="fa-explain">{data.summary}</p>}
          <div className="fa-counts">{counts.map(([k, n]) => <span key={k}><span className={`pip ${VERDICT[k].key}`} />{n} {VERDICT[k].label}</span>)}</div>
          {located && located.found > 0 && (
            <div className="fa-seg">
              <button className={view === "article" ? "on" : ""} onClick={() => setView("article")}>Article</button>
              <button className={view === "list" ? "on" : ""} onClick={() => setView("list")}>Claims ({data.claims.length})</button>
            </div>
          )}
          {showArticle ? (
            <div className="fa-article">
              <p className="fa-hint" style={{ marginTop: 0 }}>Tap a highlighted sentence to see its verdict.{located.found < data.claims.length && ` ${data.claims.length - located.found} more in Claims.`}</p>
              {data.paragraphs.map((p, i) => <MarkedPara key={i} text={p} marks={located.marks[i]} claims={data.claims} sel={sel} onPick={setSel} />)}
            </div>
          ) : (<>
          <div className="fa-mono">{data.claims.length} claims found</div>
          <div className="fa-claims">
            {data.claims.map((x, i) => (
              <button key={i} className={`fa-claim ${(VERDICT[x.label] || VERDICT.ambivalent).key}${sel === i ? " on" : ""}`} onClick={() => setSel(i)}>
                <span className="pip" />{x.statement}
              </button>
            ))}
          </div>
          </>)}
        </div>
        {c && (
          <>
            <div className="fa-scrim" onClick={() => setSel(null)} />
            <div className="fa-sheet" role="dialog">
              <button className="fa-handle" onClick={() => setSel(null)} aria-label="Close" />
              <Pill truth={c.label} big />
              <p className="fa-sheet-claim">“{c.statement}”</p>
              <p className="fa-explain">{c.explanation}</p>
              <div className="fa-row-wrap">{(c.sources || []).map((s) => <a key={s} href={s} target="_blank" rel="noreferrer" className="fa-cat">{host(s)}</a>)}</div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="fa-pad fa-top">
      <h1 className="fa-h1" style={{ fontSize: 34 }}>Scan an article</h1>
      <p className="fa-explain">Paste a link. We pull the main claims from the page and check each one.</p>
      <div className="fa-url"><Icon d={I.link} size={18} /><input type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" /></div>
      <button className="fa-btn" onClick={() => scan()} disabled={busy || !url.trim()}>{busy ? "Reading the article…" : "Scan article"}</button>
      {busy && <p className="fa-hint">This takes 10–20 seconds.</p>}
      {err && <div className="fa-error">{err}</div>}
      <div className="fa-tipcard">
        <div className="fa-mono">Tip</div>
        <p><strong>Android:</strong> in Chrome, tap <strong>Share</strong> and pick <strong>FactOrNot</strong>. The scan starts here automatically.</p>
        <p><strong>iPhone:</strong> tap <strong>Share → Copy</strong>, then paste the link above.</p>
      </div>
    </div>
  );
}

/* ---------- History tab ---------- */
function HistoryTab() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const { data } = await supabase.from("saved_checks").select("*").order("created_at", { ascending: false });
    setRows(data || []);
  };
  useEffect(() => { load(); }, []);

  const list = useMemo(() => (rows || []).filter((r) => filter === "all" || (VERDICT[r.truth] || {}).key === filter), [rows, filter]);

  if (open) return (
    <Detail item={open} onBack={() => setOpen(null)} onDelete={async () => {
      if (!window.confirm("Delete this saved check?")) return;
      await supabase.from("saved_checks").delete().eq("id", open.id);
      setOpen(null); load();
    }} />
  );

  return (
    <div className="fa-pad fa-top">
      <h1 className="fa-h1" style={{ fontSize: 34 }}>Your <em>checks</em></h1>
      <div className="fa-seg">
        {["all", "fact", "mixed", "not"].map((k) => (
          <button key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>
            {k !== "all" && <span className={`pip ${k}`} />}{k === "all" ? "All" : k[0].toUpperCase() + k.slice(1)}
          </button>
        ))}
      </div>
      {rows === null ? <p className="fa-hint">Loading…</p> :
        !list.length ? <p className="fa-hint">Nothing here yet. Run a check and it’ll appear automatically.</p> :
        <div>
          {list.map((r) => {
            const v = VERDICT[r.truth] || VERDICT.ambivalent;
            return (
              <button key={r.id} className="fa-hist" onClick={() => setOpen(r)}>
                <span className={`bar ${v.key}`} />
                <span className="grow">
                  <span className="claim">“{r.claim}”</span>
                  <span className="meta">{v.label} · {cat(r.category)} · {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                </span>
                <Icon d={I.chev} size={18} />
              </button>
            );
          })}
        </div>}
    </div>
  );
}

/* ---------- Account tab ---------- */
function AccountTab({ user, signOut }) {
  const provider = user?.app_metadata?.provider === "google" ? "Google" : "email";
  return (
    <div className="fa-pad fa-top">
      <div className="fa-user">
        <span className="fa-avatar">{(user?.email || "?")[0].toUpperCase()}</span>
        <div><div className="email">{user?.email}</div><div className="fa-hint" style={{ margin: 0 }}>Signed in with {provider}</div></div>
      </div>
      <div className="fa-mono">FactOrNot</div>
      <div className="fa-list" style={{ marginBottom: 22 }}>
        <Link to="/about" className="fa-list-row"><span className="grow">Methodology</span><Icon d={I.chev} size={18} /></Link>
        <Link to="/" className="fa-list-row"><span className="grow">Open the full website</span><Icon d={I.chev} size={18} /></Link>
      </div>
      <button className="fa-btn ghost danger" onClick={signOut}>Sign out</button>
    </div>
  );
}

/* ---------- Shell ---------- */
export default function AppShell() {
  const { user, signOut } = useAuth();
  const [params, setParams] = useSearchParams();
  // Android share target sends ?url= or ?text= (sometimes the link is inside text)
  const sharedUrl = params.get("url") || (params.get("text") || "").match(/https?:\/\/\S+/)?.[0] || "";
  const [tab, setTab] = useState(sharedUrl ? "scan" : "check");

  useEffect(() => { if (sharedUrl) setParams({}, { replace: true }); }, []); // eslint-disable-line

  const tabs = [["check", "Check", I.check], ["scan", "Scan", I.scan], ["history", "History", I.hist], ["account", "Account", I.acct]];
  return (
    <div className="fa-app">
      <main className="fa-screen">
        {tab === "check" && <CheckTab user={user} />}
        {tab === "scan" && <ScanTab initialUrl={sharedUrl} />}
        {tab === "history" && <HistoryTab />}
        {tab === "account" && <AccountTab user={user} signOut={signOut} />}
      </main>
      <nav className="fa-tabs">
        {tabs.map(([k, l, d]) => (
          <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>
            <Icon d={d} sw={tab === k ? 2.2 : 1.8} />{l}
          </button>
        ))}
      </nav>
    </div>
  );
}
